/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 데이터 그룹 클래스를 정의한다.
 *
 * @file /src/models/store/Group.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 11.
 */
import { Chunks } from '@utils/Chunks';
import { Stores } from '@models/store/Store';
import { Record } from '@models/store/Record';
import { Format } from '@utils/Format';

export class Group {
    /**
     * 그룹 고유값
     */
    id: any = 'none';

    /**
     * 그룹 내 레코드 아이디(정렬 및 필터링된 아이디)
     */
    recordIds: string[] = [];

    /**
     * 데이터 정렬방식
     */
    sorters: { [field: string]: 'ASC' | 'DESC' | any[] } = null;

    /**
     * 데이터 그룹을 정의한다.
     *
     * @param store - 데이터스토어 인스턴스
     * @param id - 그룹 고유값
     */
    constructor(
        public store: Stores,
        id: string = 'none'
    ) {
        this.id = id;
    }

    /**
     * 데이터 레코드 아이디를 추가한다.
     *
     * @param recordId - 레코드 아이디
     */
    addRecordId(recordId: string): void {
        this.recordIds.push(recordId);
    }

    /**
     * 데이터 레코드를 초기화한다.
     */
    resetRecordId(): void {
        this.recordIds = [];
    }

    /**
     * 그룹 내 레코드 아이디 목록을 가져온다.
     *
     * @returns 그룹 내 레코드 아이디 목록
     */
    getRecordIds(): string[] {
        return this.recordIds;
    }

    /**
     * 그룹 내 레코드 목록을 가져온다.
     *
     * @returns 그룹 내 레코드 목록
     */
    getRecords(): Record[] {
        return this.recordIds.map((id: string) => this.store.records.get(id));
    }

    /**
     * 그룹 내 레코드 목록을 정렬한다.
     *
     * @param sorters - 정렬방식
     */
    async sort(sorters: { [field: string]: 'ASC' | 'DESC' | any[] }): Promise<void> {
        if (Format.isEqual(this.sorters, sorters, true) == true) {
            return;
        }

        this.sorters = { ...sorters };
        await this.#sort();
    }

    /**
     * 그룹을 초기화한다.
     */
    reset(): void {
        this.recordIds = [];
        this.sorters = null;
    }

    /**
     * 그룹 내 레코드 목록을 정렬한다.
     */
    async #sort(): Promise<void> {
        const chunks = new Chunks<Record>(this.getRecords());
        const records = await chunks.sort((left, right) => {
            return Format.sorter(this.sorters, left.getData(), right.getData());
        });

        this.recordIds = [];
        const promises: Promise<void>[] = [];
        for (const record of records) {
            this.recordIds.push(record.getId());
            promises.push(record.sort(this.sorters));
        }
        await Promise.all(promises);
    }
}
